// redux/profileSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// ─── Async Thunks ────────────────────────────────────────────────────────────

// Fetch all profile data
export const fetchProfileData = createAsyncThunk(
  'profile/fetchProfileData',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      // console.log('Fetching profile data with token:', token); // Debug log

      const response = await fetch(
        'https://evercorenutrition.com/ev_admin/api/customer/profile/',
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
        return rejectWithValue('Session expired');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch profile data');
      }

      const data = await response.json();
      // console.log('Profile data fetched:', data); // Debug log
      return data;
    } catch (error) {
      console.error('Fetch profile error:', error);
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Edit profile - FIXED
export const editProfile = createAsyncThunk(
  'profile/editProfile',
  async ({ customer_id, token, profileData }, { rejectWithValue }) => {
    try {
      // Log the data being sent for debugging


      const response = await fetch(
        'https://evercorenutrition.com/ev_admin/api/customer/profile/edit/',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: customer_id,
            first_name: profileData.first_name || '',
            last_name: profileData.last_name || '',
            email: profileData.email || '',
            phone: profileData.phone || '',
            gender: profileData.gender || '',
          }),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Edit profile error:', data);
        return rejectWithValue(data?.message || 'Failed to update profile');
      }

      // console.log('Edit profile success:', data);
      return data;
    } catch (error) {
      console.error('Edit profile network error:', error);
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Upload profile picture
export const uploadProfilePicture = createAsyncThunk(
  'profile/uploadProfilePicture',
  async (file, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('profile_pic', file);

      const response = await fetch(
        'https://evercorenutrition.com/ev_admin/api/customer/profile/upload-picture/',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload profile picture');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Remove profile picture
export const removeProfilePicture = createAsyncThunk(
  'profile/removeProfilePicture',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(
        'https://evercorenutrition.com/ev_admin/api/customer/profile/remove-picture/',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to remove profile picture');
      }

      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Add address - FIXED to match API structure
export const addAddress = createAsyncThunk(
  'profile/addAddress',
  async (addressData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');

      // console.log('Adding address with data:', addressData);

      const response = await fetch(
        'https://evercorenutrition.com/ev_admin/api/customer/addresses/',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(addressData),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Add address error:', data);
        return rejectWithValue(data?.message || 'Failed to add address');
      }

      // console.log('Add address success:', data);
      return data;
    } catch (error) {
      console.error('Add address network error:', error);
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Edit address - FIXED
export const editAddress = createAsyncThunk(
  'profile/editAddress',
  async ({ addressId, addressData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');

      // console.log('Editing address:', addressId, 'with data:', addressData);

      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/addresses/${addressId}/`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(addressData),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Edit address error:', data);
        return rejectWithValue(data?.message || 'Failed to update address');
      }

      // console.log('Edit address success:', data);
      return { addressId, addressData: data };
    } catch (error) {
      console.error('Edit address network error:', error);
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Delete address
export const deleteAddress = createAsyncThunk(
  'profile/deleteAddress',
  async (addressId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/addresses/${addressId}/`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete address');
      }

      return addressId;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState = {
  // Profile data
  profile: {
    name: '',
    email: '',
    phone: '',
    gender: '',
    profilePic: 'https://cdn-icons-png.flaticon.com/512/149/149071.png',
    id: null, // Add customer ID to profile state
  },
  
  // Collections
  addresses: [],
  payments: [],
  orders: [],
  wishlist: [],
  pendingOrders: [],
  refunds: [],
  
  // Default addresses
  billingAddress: null,
  shippingAddress: null,
  
  // UI States
  loading: false,
  initialLoading: true,
  error: null,
  successMessage: null,
  
  // Action-specific loading states
  actionsLoading: {
    editProfile: false,
    uploadPicture: false,
    removePicture: false,
    addAddress: false,
    editAddress: false,
    deleteAddress: false,
  },
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    // Clear messages
    clearProfileMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
    
    // Reset state (e.g., on logout)
    resetProfileState() {
      return initialState;
    },
    
    // Manually update profile data (for optimistic updates)
    updateProfileData(state, action) {
      state.profile = { ...state.profile, ...action.payload };
    },
    
    // Clear action loading state
    clearActionLoading(state, action) {
      if (action.payload) {
        state.actionsLoading[action.payload] = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ─── Fetch Profile Data ────────────────────────────────────────────
      .addCase(fetchProfileData.pending, (state) => {
        state.initialLoading = true;
        state.error = null;
      })
      .addCase(fetchProfileData.fulfilled, (state, action) => {
        state.initialLoading = false;
        state.loading = false;
        
        const data = action.payload;
        
        if (data.customer) {
          state.profile = {
            name: data.customer.full_name || `${data.customer.first_name || ''} ${data.customer.last_name || ''}`.trim() || '',
            email: data.customer.email || '',
            phone: data.customer.phone || '',
            gender: data.customer.gender || 'Not specified',
            profilePic: data.customer.profile_pic || state.profile.profilePic,
            id: data.customer.id || null, // Store customer ID
          };
        }
        
        if (data.addresses) {
          state.addresses = data.addresses;
          const defaultBilling = data.addresses.find((addr) => addr.is_default_billing);
          const defaultShipping = data.addresses.find((addr) => addr.is_default_shipping);
          state.billingAddress = defaultBilling || null;
          state.shippingAddress = defaultShipping || null;
        }
        
        if (data.orders) {
          state.orders = data.orders;
          state.pendingOrders = data.orders.filter(
            (order) => order.status === 'pending' || order.status === 'Pending'
          );
        }
        
        if (data.wishlist) state.wishlist = data.wishlist;
        if (data.payment_methods) state.payments = data.payment_methods;
        if (data.refunds) state.refunds = data.refunds;
      })
      .addCase(fetchProfileData.rejected, (state, action) => {
        state.initialLoading = false;
        state.loading = false;
        state.error = action.payload || 'Failed to fetch profile data';
      })
      
      // ─── Edit Profile ───────────────────────────────────────────────────
      .addCase(editProfile.pending, (state) => {
        state.actionsLoading.editProfile = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(editProfile.fulfilled, (state, action) => {
        state.actionsLoading.editProfile = false;
        state.successMessage = 'Profile updated successfully.';
        
        const customer = action.payload?.customer;
        if (customer) {
          state.profile = {
            ...state.profile,
            name: customer.full_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || state.profile.name,
            email: customer.email || state.profile.email,
            phone: customer.phone || state.profile.phone,
            gender: customer.gender || state.profile.gender,
            profilePic: customer.profile_pic || state.profile.profilePic,
            id: customer.id || state.profile.id,
          };
        }
      })
      .addCase(editProfile.rejected, (state, action) => {
        state.actionsLoading.editProfile = false;
        state.error = action.payload || 'Failed to update profile';
      })
      
      // ─── Upload Profile Picture ─────────────────────────────────────────
      .addCase(uploadProfilePicture.pending, (state) => {
        state.actionsLoading.uploadPicture = true;
        state.error = null;
      })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => {
        state.actionsLoading.uploadPicture = false;
        state.successMessage = 'Profile picture updated successfully.';
        
        if (action.payload?.profile_pic) {
          state.profile.profilePic = action.payload.profile_pic;
        }
      })
      .addCase(uploadProfilePicture.rejected, (state, action) => {
        state.actionsLoading.uploadPicture = false;
        state.error = action.payload || 'Failed to upload profile picture';
      })
      
      // ─── Remove Profile Picture ─────────────────────────────────────────
      .addCase(removeProfilePicture.pending, (state) => {
        state.actionsLoading.removePicture = true;
        state.error = null;
      })
      .addCase(removeProfilePicture.fulfilled, (state) => {
        state.actionsLoading.removePicture = false;
        state.successMessage = 'Profile picture removed.';
        state.profile.profilePic = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
      })
      .addCase(removeProfilePicture.rejected, (state, action) => {
        state.actionsLoading.removePicture = false;
        state.error = action.payload || 'Failed to remove profile picture';
      })
      
      // ─── Add Address ────────────────────────────────────────────────────
      .addCase(addAddress.pending, (state) => {
        state.actionsLoading.addAddress = true;
        state.error = null;
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        state.actionsLoading.addAddress = false;
        state.successMessage = 'Address added successfully.';
        state.addresses.push(action.payload);
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.actionsLoading.addAddress = false;
        state.error = action.payload || 'Failed to add address';
      })
      
      // ─── Edit Address ───────────────────────────────────────────────────
      .addCase(editAddress.pending, (state) => {
        state.actionsLoading.editAddress = true;
        state.error = null;
      })
      .addCase(editAddress.fulfilled, (state, action) => {
        state.actionsLoading.editAddress = false;
        state.successMessage = 'Address updated successfully.';
        
        const { addressId, addressData } = action.payload;
        const index = state.addresses.findIndex(addr => addr.id === addressId);
        if (index !== -1) {
          state.addresses[index] = { ...state.addresses[index], ...addressData };
        }
      })
      .addCase(editAddress.rejected, (state, action) => {
        state.actionsLoading.editAddress = false;
        state.error = action.payload || 'Failed to update address';
      })
      
      // ─── Delete Address ─────────────────────────────────────────────────
      .addCase(deleteAddress.pending, (state) => {
        state.actionsLoading.deleteAddress = true;
        state.error = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.actionsLoading.deleteAddress = false;
        state.successMessage = 'Address removed successfully.';
        state.addresses = state.addresses.filter(addr => addr.id !== action.payload);
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.actionsLoading.deleteAddress = false;
        state.error = action.payload || 'Failed to delete address';
      });
  },
});

export const { 
  clearProfileMessages, 
  resetProfileState, 
  updateProfileData,
  clearActionLoading 
} = profileSlice.actions;

export default profileSlice.reducer;